/**
 * API Client — Council Extension
 *
 * Provides analyzeCouncil(payload) for POST /council/analyze.
 * Includes API key header, 10s timeout, standardized response handling.
 * Strictly READ-ONLY — no trade execution endpoints.
 */

import type { CouncilAnalysisRequest, CouncilGatewayResponse } from '../types/council';

import { apiV1Base } from './apiConfig';
// ═══════════════════════════════════════════════════════════════════════
//  CONFIG
// ═══════════════════════════════════════════════════════════════════════

function getEnv(key: string, fallback: string): string {
  try {
    return (import.meta as any).env?.[key] ?? fallback;
  } catch {
    return fallback;
  }
}

const GATEWAY_BASE = apiV1Base();
const API_KEY = getEnv('VITE_API_KEY', 'adversyn-dev-key-2026');
const COUNCIL_TIMEOUT_MS = 10_000;

// ═══════════════════════════════════════════════════════════════════════
//  CORE FETCH WITH TIMEOUT
// ═══════════════════════════════════════════════════════════════════════

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timer);
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  STANDARDIZED RESPONSE HANDLING
// ═══════════════════════════════════════════════════════════════════════

function buildHeaders(debug = false): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
  };
  if (debug) {
    headers['x-debug'] = 'true';
  }
  return headers;
}

// ═══════════════════════════════════════════════════════════════════════
//  analyzeCouncil — POST /council/analyze
// ═══════════════════════════════════════════════════════════════════════

export async function analyzeCouncil(
  payload: CouncilAnalysisRequest,
  options?: { debug?: boolean },
): Promise<CouncilGatewayResponse> {
  const url = `${GATEWAY_BASE}/council/analyze`;
  const debug = options?.debug ?? false;

  const body = JSON.stringify({
    query: `Analyze ${payload.symbol} for ${payload.debateType}`,
    symbol: payload.symbol,
    asset_type: payload.assetType,
    debate_type: payload.debateType,
    timeframe: payload.timeframe,
  });

  let response: Response;
  try {
    response = await fetchWithTimeout(
      url,
      { method: 'POST', headers: buildHeaders(debug), body },
      COUNCIL_TIMEOUT_MS,
    );
  } catch (e) {
    throw new CouncilApiError(
      'Council service unreachable. Please try again later.',
      'COUNCIL_NETWORK_ERROR',
      0,
    );
  }

  // Read body as text first so we never throw a JSON parser error.
  // (The lockdown route can return HTML; some upstreams return plain text.)
  const rawText = await response.text();
  let json: CouncilGatewayResponse | undefined;
  try {
    json = rawText ? (JSON.parse(rawText) as CouncilGatewayResponse) : undefined;
  } catch {
    json = undefined;
  }

  if (debug) {
    console.log('[Council API Debug] status:', response.status, 'json:', json, 'raw_len:', rawText?.length ?? 0);
  }

  // Non-JSON response from server: surface a clean, structured error.
  // Do NOT include the raw HTML in the user-visible message.
  if (!json) {
    const status = response.status;
    let code = 'COUNCIL_NON_JSON_RESPONSE';
    let message = 'Council analysis is currently unavailable.';
    if (status === 401) {
      code = 'COUNCIL_UNAUTHORIZED';
      message = 'You are not signed in to Council analysis.';
    } else if (status === 403) {
      code = 'COUNCIL_FORBIDDEN';
      message = 'Council analysis is not available on this account.';
    } else if (status >= 500) {
      code = 'COUNCIL_SERVER_ERROR';
      message = 'Council service is temporarily unavailable. Please try again.';
    }
    throw new CouncilApiError(message, code, status);
  }

  // ── New contract (backend route at /council/analyze) ──
  // The backend returns a top-level `status` field. Map it through:
  //   "OK"          → return the response (real analysis result)
  //   "UNAVAILABLE" → throw with the backend's code/message
  //   "LIMITED"     → throw with the backend's code/message
  //   "ERROR"       → throw with the backend's code/message (e.g. invalid symbol)
  //
  // The 4xx-status JSON path is handled here too: backend uses 400 for
  // invalid input but still embeds a structured {status,code,message}.
  const newContractStatus = (json as any)?.status;
  if (typeof newContractStatus === 'string' && newContractStatus.length > 0) {
    const code = (json as any)?.code ?? 'COUNCIL_UNKNOWN';
    const message = (json as any)?.message ?? 'Council analysis is unavailable.';
    // 2026-05-14: Accept LIMITED as a successful result. The backend
    // returns LIMITED when only one provider participated (e.g. only
    // OpenAI/GPT is configured). It is a real analysis -- the synthesis
    // is honest about single-provider scope -- and the store should not
    // retry-loop it as if it were an error.
    if (newContractStatus === 'OK' || newContractStatus === 'LIMITED') {
      return json;
    }
    throw new CouncilApiError(message, code, response.status);
  }

  // ── Old contract fallback (gateway/legacy: success+data envelope) ──
  if (!response.ok) {
    const status = response.status;
    let message = json?.error?.message ?? '';
    if (!message) {
      if (status === 401) message = 'You are not signed in to Council analysis.';
      else if (status === 403) message = 'Council analysis is not available on this account.';
      else if (status >= 500) message = 'Council service is temporarily unavailable. Please try again.';
      else message = `Council request failed (HTTP ${status}).`;
    }
    throw new CouncilApiError(message, json?.error?.code ?? 'HTTP_ERROR', status);
  }

  if (!json.success) {
    throw new CouncilApiError(
      json?.error?.message ?? 'Council analysis was not completed.',
      json?.error?.code ?? 'API_ERROR',
      response.status,
    );
  }

  return json;
}

// ═══════════════════════════════════════════════════════════════════════
//  pollCouncilDeliberation — GET /council/analyze/deliberate?request_id=...
//  (2026-05-19 two-phase async Council)
// ═══════════════════════════════════════════════════════════════════════

export interface CouncilDeliberationPollResult {
  status: 'pending' | 'completed' | 'unavailable' | 'unknown';
  // Present when status === 'completed' or 'unavailable':
  final_decision?: 'BUY' | 'SELL' | 'HOLD' | 'WATCHLIST' | 'NO_TRADE' | 'BLOCKED';
  confidence?: number;
  time_horizon?: 'intraday' | '1d' | '1w' | 'swing' | 'watchlist';
  decision_reason?: string;
  supporting_evidence?: string[];
  risk_flags?: string[];
  missing_data?: string[];
  agreements?: string[];
  disagreements?: string[];
  what_would_change_decision?: string[];
  research_context?: string;
  deliberation_provider?: string;
  deliberation_latency_ms?: number;
  // Present when status === 'unavailable':
  deliberator_error_code?: string;
  deliberator_error_message?: string;
  // Always present once a record exists:
  advisory_only?: boolean;
  advisory_note?: string;
  request_id?: string;
  generated_at?: string;
  // Present when status === 'unknown' (no record found / TTL'd out):
  code?: string;
  message?: string;
}

export async function pollCouncilDeliberation(
  requestId: string,
): Promise<CouncilDeliberationPollResult> {
  if (!requestId) {
    throw new CouncilApiError('Missing request_id', 'POLL_MISSING_ID', 400);
  }
  const url = `${GATEWAY_BASE}/council/analyze/deliberate?request_id=${encodeURIComponent(requestId)}`;
  let response: Response;
  try {
    // Poll fetch is bounded to 8s. The route itself is read-only and fast;
    // 8s leaves headroom even if nginx is sluggish, but is short enough to
    // resume the next poll attempt promptly.
    response = await fetchWithTimeout(url, { method: 'GET' }, 8000);
  } catch (e) {
    // Network failure on poll is NOT fatal — the caller decides whether to
    // keep polling or abandon. Return a synthetic 'unknown' so the UI shows
    // "deliberation unavailable" rather than a hard crash.
    return {
      status: 'unknown',
      code: 'POLL_NETWORK_ERROR',
      message: e instanceof Error ? e.message : 'Poll request failed.',
    };
  }
  const rawText = await response.text();
  let json: any;
  try {
    json = rawText ? JSON.parse(rawText) : {};
  } catch {
    json = {};
  }
  const status = (json?.status || '').toString().toLowerCase();
  if (status === 'pending' || status === 'completed' || status === 'unavailable' || status === 'unknown') {
    return { ...json, status: status as CouncilDeliberationPollResult['status'] };
  }
  // Anything else (e.g. 403 lockdown JSON) — surface as unknown so caller falls back gracefully.
  return {
    status: 'unknown',
    code: (json?.code as string) || 'POLL_UNEXPECTED_RESPONSE',
    message: (json?.message as string) || 'Unexpected deliberation poll response.',
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  ERROR CLASS
// ═══════════════════════════════════════════════════════════════════════

export class CouncilApiError extends Error {
  code: string;
  statusCode: number;

  constructor(message: string, code: string, statusCode: number) {
    super(message);
    this.name = 'CouncilApiError';
    this.code = code;
    this.statusCode = statusCode;
  }
}
